import { size } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { InsightPage } from "@radarly/models/insight-pages/model";
import { getUpdatedPagesFromPageAndSnapshots } from "@radarly/models/insight-pages/snapshot";
import {
  InsightPageRepo,
  InsightPageRepositoryUID,
} from "../repositories/InsightPageRepo";

export function useSaveInsightPage() {
  const repository = useInjection<InsightPageRepo>(InsightPageRepositoryUID);
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading, isError } = useMutation(
    async ({
      project,
      page,
      ref,
      template,
    }: {
      project: number;
      page: InsightPage;
      ref?: InsightPage;
      template?: string;
    }) => {
      if (page.id && ref) {
        const recurringSnapshots = await repository.getRecurringSnapshots(
          project,
          page.id,
          true
        );
        if (size(recurringSnapshots)) {
          await repository.batchUpdateRecurringSnapshots(
            project,
            page.id,
            getUpdatedPagesFromPageAndSnapshots(
              page,
              recurringSnapshots,
              ref.timezone
            )
          );
        }
      }

      return repository.setInsightPage(project, page, template);
    },
    {
      onSuccess: (page, { project }) => {
        queryClient.invalidateQueries(["/insight-pages/search"]);
        queryClient.invalidateQueries(["/insight-pages/list"]);
        queryClient.setQueryData(
          ["/insight-page/get", { project, id: page.id }],
          page
        );
      },
    }
  );
  const setInsightPage = (data: {
    project: number;
    page: InsightPage;
    ref?: InsightPage;
    template?: string;
  }) => {
    return mutateAsync(data);
  };
  return {
    setInsightPage,
    loading: isLoading,
    error: isError,
  };
}
